import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Divider } from 'antd';
import { useTheme } from '@emotion/react';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import Tag from '../../components/common/Tag';
import {
  checkPermission,
  convertToHoursAndMinutes,
  Description
} from '../../utils/common_functions';
import { actionTypeEnums, reportingDonePoints, ReportingType } from '../../utils/constant';
import {
  ClickWrapper,
  DarkText,
  DeleteIconBox,
  EditIconBox,
  FlexWrapper,
  PurpleText
} from '../../theme/common_style';
import dayjs from 'dayjs';
import { DeleteIcon, EditIcon } from '../../theme/SvgIcons';
import { useSelector } from 'react-redux';
import { useCurrentModule } from '../../hooks/useCurrentModule';

const Cards = ({ report, empName, date, isCardOpen, setIsCardOpen, handleReport }) => {
  const theme = useTheme();
  const { permissions } = useSelector((state) => state?.userInfo?.data);
  let permissionSection = useCurrentModule();
  const { UPDATE, DELETE } = actionTypeEnums;
  const canUpdate = checkPermission(permissionSection, UPDATE, permissions);
  const canDelete = checkPermission(permissionSection, DELETE, permissions);

  const expanded = isCardOpen === report?.reporting_id;

  const handleExpand = useCallback(() => {
    setIsCardOpen(expanded ? null : report?.reporting_id);
  }, [expanded, setIsCardOpen, report]);

  return (
    <ExpandCollapseWrapper
      title={empName}
      date={date}
      expanded={expanded}
      onToggle={handleExpand}
      report={report}
      canUpdate={canUpdate}
      canDelete={canDelete}
      handleReport={handleReport}>
      <ContentWrapper>
        <FlexWrapperReport direction="column" justify="space-between" width="64%" align="start">
          <FlexWrapperReport
            direction="column"
            align="start"
            width="100%"
            justify="start"
            cursor="default">
            <Divider style={{ borderColor: '#F1F1F1', margin: 0, borderWidth: '1.5px' }} />
            <DarkText size="15px" style={{ textAlign: 'left' }}>
              <Description htmlString={report?.description} />
            </DarkText>
          </FlexWrapperReport>
          {/* <ReportingDate>
            Report for {dayjs(report?.reporting_date).format('DD MMM, YYYY')}
          </ReportingDate> */}
        </FlexWrapperReport>
        <FlexWrapperReport
          cursor="default"
          direction="column"
          gap="10px"
          justify="start"
          align="start"
          className="done-points">
          <span style={{ fontSize: '18px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans' }}>
            Done Points
          </span>
          {reportingDonePoints?.map((option) => (
            <FlexWrapper
              key={option}
              completed={(report?.checklists || []).includes(option)}
              gap="6px"
              align="flex-start"
              justify="flex-start"
              cursor="default"
              wrap="nowrap">
              <input
                type="checkbox"
                checked={(report?.checklists || []).includes(option)}
                readOnly
                tabIndex={-1}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: theme?.primaryColor,
                  cursor: 'not-allowed'
                }}
              />
              <span
                style={{
                  textAlign: 'start',
                  fontSize: '15px',
                  fontFamily: 'Plus Jakarta Sans'
                }}>
                {option}
              </span>
            </FlexWrapper>
          ))}
        </FlexWrapperReport>
      </ContentWrapper>
    </ExpandCollapseWrapper>
  );
};

const ContentWrapper = styled(FlexWrapper)`
  align-items: unset;
  flex-wrap: nowrap;
  background-color: #fff;
  border-radius: 12px;
  padding: 0 20px 15px;
  margin: 0;
  gap: 20px;
  cursor: default;
  width: 100%;
  @media (max-width: 1024px) {
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: start;
  }
  @media (max-width: 620px) {
    padding: 10px;
  }
`;

// const ReportingDate = styled.div`
//   font-size: 13px;
//   width: 100%;
//   text-align: start;
//   color: #646464cf;
//   border-top: 1px solid #e2e2e2cf;
//   padding-top: 10px;
//   font-weight: 500;
// `;

const FlexWrapperReport = styled.div`
  width: ${(props) => props.width};
  display: flex;
  flex-wrap: ${(props) => props.wrap || 'wrap'};
  flex-direction: ${(props) => props.direction || 'row'};
  justify-content: ${(props) => props.justify || 'center'};
  align-items: ${(props) => props.align || 'center'};
  gap: ${(props) => props.gap || '10px'};
  cursor: ${(props) => props.cursor || 'auto'};
  margin: ${(props) => props.margin || '0px'};
  .done-points {
    width: 40%;
  }
  @media (max-width: 768px) {
    width: 100%;
    align-items: start;
    justify-content: start;
    .done-points {
      width: 100%;
      display: flex;
      justify-content: start;
      align-items: start;
    }
  }
`;

Cards.propTypes = {
  report: PropTypes.object.isRequired,
  empName: PropTypes.string,
  date: PropTypes.string,
  isCardOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setIsCardOpen: PropTypes.func.isRequired,
  handleReport: PropTypes.func
};
export default Cards;

const ExpandCollapseWrapper = ({
  title = '',
  date = '',
  children,
  expanded = true,
  onToggle,
  report,
  canUpdate,
  canDelete,
  handleReport
}) => {
  const { isEmployee } = useSelector((e) => e.userInfo);

  const billingTime = useMemo(
    () => (report?.billable ? convertToHoursAndMinutes(report?.billable_mins) : null),
    [report]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  const [showCaretDown, setShowCaretDown] = useState(expanded);
  const timeoutRef = useRef();
  const TRANSITION_DURATION = 100;

  useEffect(() => {
    if (expanded) {
      setShowCaretDown(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } else {
      timeoutRef.current = setTimeout(() => {
        setShowCaretDown(false);
      }, TRANSITION_DURATION);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [expanded]);

  const showEdit = () => {
    const reportDate = dayjs.utc(report?.created_at).local();
    const today = dayjs();
    const daysDifference = today.diff(reportDate, 'day');

    // Always allow editing on the same day
    if (reportDate.isSame(today, 'day')) {
      if (isEmployee) {
        return true;
      }
      if (!isEmployee && report?.reporting_type === ReportingType.MANUAL) {
        return true;
      }
      return false;
    }

    // For manual reports, allow editing up to 7 days old
    if (daysDifference <= 6 && report?.reporting_type === ReportingType.MANUAL) {
      return !isEmployee; // Only allow admin to edit past-day manual reports
    }

    return false;
  };

  return (
    <Wrapper>
      <Header
        onClick={onToggle}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        onKeyDown={handleKeyDown}>
        <FlexWrappers direction="column" align="flex-start">
          <Title>
            {title}{' '}
            {!dayjs(report?.created_at).isSame(dayjs(report?.updated_at)) && (
              <EditedText>(Edited)</EditedText>
            )}
            {report?.reporting_type === ReportingType.MANUAL && <EditedText>(Manual)</EditedText>}
          </Title>
          <PurpleText size="13px" width="fit-content">
            <strong style={{ color: '#000000cf' }}>Submitted On: </strong> {date}
          </PurpleText>
        </FlexWrappers>
        <FlexWrapp gap="15px">
          {report?.billable && report?.billable_mins && (
            <BillingTag>
              Billing: {billingTime?.hours} Hrs {billingTime?.minutes} Mins
            </BillingTag>
          )}
          {showEdit() && (
            <>
              {canUpdate && (
                <ClickWrapper
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReport({ isOpen: true, report });
                  }}>
                  <EditIconBox canUpdate={canUpdate}>
                    <EditIcon />
                  </EditIconBox>
                </ClickWrapper>
              )}
              {canDelete && (
                <ClickWrapper
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReport({ isOpen: true, report, isDelete: true });
                  }}>
                  <DeleteIconBox canDelete={canDelete}>
                    <DeleteIcon />
                  </DeleteIconBox>
                </ClickWrapper>
              )}
            </>
          )}

          {showCaretDown ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </FlexWrapp>
      </Header>
      <Content expanded={expanded}>{children}</Content>
    </Wrapper>
  );
};

ExpandCollapseWrapper.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  expanded: PropTypes.bool,
  date: PropTypes.string,
  onToggle: PropTypes.func,
  report: PropTypes.object,
  canUpdate: PropTypes.bool,
  canDelete: PropTypes.bool,
  handleReport: PropTypes.func
};

const BillingTag = styled(Tag)`
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.primaryColor};
  color: #ffffff;
`;

const EditedText = styled.span`
  font-size: 14px;
  font-weight: 500;
  font-family: 'Plus Jakarta Sans';
  text-align: left;
  color: ${({ theme }) => theme.primaryColor};
  margin-left: 4px;
`;

const Wrapper = styled.div`
  border: 1px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const Title = styled.div`
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans';
  text-align: left;
`;

const Header = styled.div`
  background-color: #ffffff;
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  outline: none;
  &:focus {
    box-shadow: 0 0 0 2px #a3bffa;
  }

  @media (max-width: 620px) {
    flex-direction: column-reverse;
    align-items: start;
    justify-content: start;
    gap: 12px;
    .css-8kmmnd {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const Content = styled.div`
  max-height: ${({ expanded }) => (expanded ? '2500px' : '0')};
  overflow: hidden;
  transition: max-height 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #ffffff;
`;
const FlexWrappers = styled.div`
  width: ${(props) => props.width};
  display: flex;
  flex-wrap: ${(props) => props.wrap || 'wrap'};
  flex-direction: ${(props) => props.direction || 'row'};
  justify-content: ${(props) => props.justify || 'center'};
  align-items: ${(props) => props.align || 'center'};
  gap: ${(props) => props.gap || '0px'};
  cursor: ${(props) => props.cursor || 'auto'};
  margin: ${(props) => props.margin || '0px'};
`;
const FlexWrapp = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 15px;
  cursor: auto;
  margin: 0px;
  @media (max-width: 620px) {
    width: 100%;
    justify-content: space-between !important;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }
`;
